import { useQueryClient } from '@tanstack/react-query';
import { Button, Form, ModalForm, PopupContext, ScrollableTabs } from 'components';
import VanityUrlAvailabilityInput from 'components/Form/VanityUrlAvailabilityInput';
import { MediaUpload } from 'components/Form/inputs/MediaUpload/MediaUpload';
import countries from 'constants/countries';
import LocationImagesPreview from 'pages/Location/components/preview/LocationImagesPreview';
import PropTypes from 'prop-types';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { media } from 'utils/rwd';
import {
  useUpdateLocation,
  useUpdateLocationAddress,
  useUpdateLocationOpeningHours,
} from 'api/locations/hooks';
import LOCATIONS_KEYS from 'api/locations/locations.keys';
import { useUploadMedia } from 'hooks/useUploadMedia';
import {
  buildSocialLink,
  getSocialInputValue,
  getSocialPrependText,
  SOCIALS_CONFIG,
} from 'constants/socialsConfig';
import { getPrependFromPublicUrl } from '../../utils/getPrependFromPublicUrl';
import OpeningHoursEditor from '../../pages/Location/components/openingHours/OpeningHoursEditor';
import {
  buildOpeningHoursPayload,
  getInitialOpeningHours,
  openingHoursValidationSchema,
} from '../../pages/Location/components/openingHours/openingHours';
import validationSchemas from './schemas';

const createEmptyAdditionalLink = () => ({ name: '', url: '' });

const normalizeValue = (value) => value ?? '';

const getInitialAdditionalLinks = (links) => {
  if (!Array.isArray(links) || links.length === 0) return [createEmptyAdditionalLink()];

  return links.map((link) => ({
    name: link?.name || '',
    url: link?.url || '',
  }));
};

const normalizeAdditionalLinks = (links = []) =>
  links
    .map((link) => ({
      name: link?.name?.trim() || '',
      url: link?.url?.trim() || '',
    }))
    .filter(({ name, url }) => name && url);

const moveAdditionalLink = (links, index, direction) => {
  const nextLinks = [...(links || [])];
  const targetIndex = index + direction;

  if (targetIndex < 0 || targetIndex >= nextLinks.length) return nextLinks;

  [nextLinks[index], nextLinks[targetIndex]] = [nextLinks[targetIndex], nextLinks[index]];
  return nextLinks;
};

const EditLocationForm = ({ location }) => {
  const { t } = useTranslation('ownerPortal', { keyPrefix: 'locations' });
  const { closePopup } = useContext(PopupContext);
  const queryClient = useQueryClient();
  const { mutateAsync: updateLocation, isPending: isUpdatingLocation } = useUpdateLocation();
  const { mutateAsync: updateLocationAddress, isPending: isUpdatingAddress } =
    useUpdateLocationAddress();
  const { mutateAsync: updateOpeningHours, isPending: isUpdatingOpeningHours } =
    useUpdateLocationOpeningHours();
  const { mutateAsync: uploadMediaAsync, isPending: isUploadingMedia } = useUploadMedia();
  const [activeTab, setActiveTab] = useState('info');
  const isSubmitting =
    isUploadingMedia || isUpdatingLocation || isUpdatingAddress || isUpdatingOpeningHours;
  const validationSchema = useMemo(
    () =>
      validationSchemas.location_info
        .concat(validationSchemas.location_address)
        .concat(openingHoursValidationSchema)
        .concat(validationSchemas.location_additional_links)
        .concat(validationSchemas.location_socials)
        .concat(validationSchemas.location_images),
    [],
  );

  const tabs = [
    { key: 'info', label: t('editModal.info') },
    { key: 'address', label: t('editModal.address') },
    { key: 'openingHours', label: t('editModal.openingHours') },
    { key: 'additionalLinks', label: t('editModal.additionalLinks') },
    { key: 'socials', label: t('editModal.socials') },
    { key: 'images', label: t('editModal.images') },
  ];
  const renderContentForActiveTab = (tabKey, values, setFieldValue, setFieldTouched) => {
    const setAdditionalLinks = (nextLinks) => {
      setFieldValue('additional_links', nextLinks);
      setFieldTouched('additional_links', true, false);
    };

    switch (tabKey) {
      case 'info':
        return (
          <>
            <Form.GroupVertical label={t('onboarding.nameLabel')} required>
              <Form.Input name="name" />
            </Form.GroupVertical>

            <Form.GroupVertical label={t('onboarding.descriptionLabel')}>
              <Form.Input
                as="textarea"
                name="description"
                maxLength={5000}
                style={{ resize: 'vertical', maxHeight: 200, overflow: 'auto' }}
              />
            </Form.GroupVertical>

            <Form.GroupVertical label={t('onboarding.vanityUrlLabel')} required>
              <VanityUrlAvailabilityInput
                name="vanityUrl"
                prepend={<PrependText>{getPrependFromPublicUrl(location?.public_url)}</PrependText>}
              />
            </Form.GroupVertical>

            <Form.GroupVertical
              label={t('onboarding.locationTrackingIdNameLabel', {
                name: location?.location_tracking_id_name
                  ? `(${location.location_tracking_id_name})`
                  : '',
              })}
              required
            >
              <Form.Input name="location_tracking_id" />
            </Form.GroupVertical>
          </>
        );
      case 'address':
        return (
          <>
            <Form.GroupVertical label={t('onboarding.streetLabel')} required>
              <Form.Input name="street" />
            </Form.GroupVertical>
            <Form.GroupVertical label={t('onboarding.apartmentLabel')}>
              <Form.Input name="apartment" />
            </Form.GroupVertical>
            <Form.GroupVertical label={t('onboarding.cityLabel')} required>
              <Form.Input name="city" />
            </Form.GroupVertical>
            <Form.GroupVertical label={t('onboarding.zipLabel')} required>
              <Form.Input name="zip" />
            </Form.GroupVertical>
            <Form.GroupVertical label={t('onboarding.stateLabel')}>
              <Form.Input name="state" />
            </Form.GroupVertical>
            <Form.GroupVertical label={t('onboarding.countryLabel')} required>
              <Form.MultiSelect
                isMulti={false}
                options={countries.map((country) => ({
                  value: country.code,
                  label: country.name,
                }))}
                name="country"
                required
              />
            </Form.GroupVertical>
          </>
        );
      case 'openingHours':
        return (
          <OpeningHoursEditor
            openingHours={values.opening_hours}
            setFieldValue={setFieldValue}
            setFieldTouched={setFieldTouched}
          />
        );
      case 'additionalLinks':
        return (
          <AdditionalLinksContent>
            {(values.additional_links || [createEmptyAdditionalLink()]).map((_, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <AdditionalLinkRow key={`additional-link-${index}`}>
                <AdditionalLinkField>
                  {t('editModal.additionalLinkName')}
                  <Form.Input name={`additional_links[${index}].name`} />
                </AdditionalLinkField>
                <AdditionalLinkField>
                  {t('editModal.additionalLinkUrl')}
                  <Form.Input name={`additional_links[${index}].url`} />
                </AdditionalLinkField>
                <AdditionalLinkActions>
                  <LinkActionButton
                    type="button"
                    onClick={() =>
                      setAdditionalLinks(moveAdditionalLink(values.additional_links, index, -1))
                    }
                    disabled={index === 0}
                    aria-label="Move additional link up"
                  >
                    <i className="fe fe-arrow-up" style={{ fontSize: 20 }} />
                  </LinkActionButton>
                  <LinkActionButton
                    type="button"
                    onClick={() =>
                      setAdditionalLinks(moveAdditionalLink(values.additional_links, index, 1))
                    }
                    disabled={index === (values.additional_links || []).length - 1}
                    aria-label="Move additional link down"
                  >
                    <i className="fe fe-arrow-down" style={{ fontSize: 20 }} />
                  </LinkActionButton>
                  <DeleteLinkButton
                    type="button"
                    onClick={() =>
                      setAdditionalLinks(
                        (values.additional_links || []).filter(
                          (__, linkIndex) => linkIndex !== index,
                        ),
                      )
                    }
                    aria-label="Delete additional link"
                  >
                    <i className="fe fe-trash" style={{ fontSize: 20 }} />
                  </DeleteLinkButton>
                </AdditionalLinkActions>
              </AdditionalLinkRow>
            ))}
            <AddLinkButton
              type="button"
              onClick={() =>
                setAdditionalLinks([
                  ...(values.additional_links || []),
                  createEmptyAdditionalLink(),
                ])
              }
              aria-label="Add additional link"
            >
              <i className="fe fe-plus" style={{ fontSize: 20 }} />
            </AddLinkButton>
          </AdditionalLinksContent>
        );
      case 'socials':
        return (
          <>
            {SOCIALS_CONFIG.map(({ name, label, bgColor, icon: Icon, prependText }) => (
              <Label key={name}>
                {label}
                <InputWithIconContainer>
                  <IconContainer $bgColor={bgColor}>
                    <Icon fill="#fff" />
                  </IconContainer>
                  <InputContainer>
                    <Form.Input name={name} prepend={prependText} />
                  </InputContainer>
                </InputWithIconContainer>
              </Label>
            ))}
          </>
        );
      case 'images':
        return (
          <Content>
            <ImagesPreview
              logo={
                values.logoId[0] instanceof File
                  ? URL.createObjectURL(values.logoId[0])
                  : values.logo[0]
              }
              coverImage={
                values.coverImageId[0] instanceof File
                  ? URL.createObjectURL(values.coverImageId[0])
                  : values.coverImage[0]
              }
            />

            <UploadSection>
              <MediaUploadContainer>
                <Form.GroupHorizontal label={t('onboarding.logoLabel')}>
                  <MediaUpload
                    accept={{
                      'image/png': ['.png'],
                      'image/jpeg': ['.jpg', '.jpeg'],
                      'image/webp': ['.webp'],
                    }}
                    name="logoId"
                    imageField="logo"
                    maxFiles={1}
                    withThumbnails={false}
                    withCrop
                  />
                </Form.GroupHorizontal>
              </MediaUploadContainer>

              <MediaUploadContainer>
                <Form.GroupHorizontal label={t('onboarding.coverImageLabel')}>
                  <MediaUpload
                    accept={{
                      'image/*': [],
                      'image/webp': ['.webp'],
                    }}
                    name="coverImageId"
                    imageField="coverImage"
                    maxFiles={1}
                    withThumbnails={false}
                    withCrop
                    aspectRatio={16 / 9}
                    defaultCropMode="rectangle"
                  />
                </Form.GroupHorizontal>
              </MediaUploadContainer>
            </UploadSection>
          </Content>
        );
      default:
        return null;
    }
  };

  const address = location?.address || {};
  const initialValues = {
    uid: location?.uid || undefined,
    name: location?.name || '',
    description: location?.description || '',
    vanityUrl: location?.vanity_url || '',
    location_tracking_id: location?.location_tracking_id || '',
    location_tracking_id_name: location?.location_tracking_id_name || '',
    street: address.line1 || '',
    apartment: address.line2 || '',
    city: address.city || '',
    zip: address.postal_code || '',
    state: address.state || '',
    country: address.country || '',
    opening_hours: getInitialOpeningHours(location?.opening_hours),
    facebook: getSocialInputValue(location?.facebook, getSocialPrependText('facebook')),
    instagram: getSocialInputValue(location?.instagram, getSocialPrependText('instagram')),
    x: getSocialInputValue(location?.x, getSocialPrependText('x')),
    tiktok: getSocialInputValue(location?.tiktok, getSocialPrependText('tiktok')),
    pinterest: getSocialInputValue(location?.pinterest, getSocialPrependText('pinterest')),
    additional_links: getInitialAdditionalLinks(location?.additional_links),
    logo: location.logo ? [location.logo] : [],
    coverImage: location.cover_image ? [location.cover_image] : [],
    logoId: location.logo_id ? [location.logo_id] : [],
    coverImageId: location.cover_image_id ? [location.cover_image_id] : [],
  };

  const handleSubmit = useCallback(
    async ({
      uid,
      name,
      description,
      vanityUrl,
      location_tracking_id,
      logoId,
      coverImageId,
      street,
      apartment,
      city,
      zip,
      state,
      country,
      opening_hours,
      facebook,
      instagram,
      tiktok,
      x,
      pinterest,
      additional_links,
    }) => {
      let publicLogoId = '';
      let publicCoverImageId = '';

      if (Array.isArray(logoId) && logoId.length > 0) {
        const { public_id } = await uploadMediaAsync({ media: logoId });
        publicLogoId = public_id;
      }

      if (Array.isArray(coverImageId) && coverImageId.length > 0) {
        const { public_id } = await uploadMediaAsync({ media: coverImageId });
        publicCoverImageId = public_id;
      }

      const locationPayload = {
        name,
        description,
        location_tracking_id,
        facebook: buildSocialLink(facebook, getSocialPrependText('facebook')),
        instagram: buildSocialLink(instagram, getSocialPrependText('instagram')),
        tiktok: buildSocialLink(tiktok, getSocialPrependText('tiktok')),
        x: buildSocialLink(x, getSocialPrependText('x')),
        pinterest: buildSocialLink(pinterest, getSocialPrependText('pinterest')),
        additional_links: normalizeAdditionalLinks(additional_links),
        vanity_url: vanityUrl,
        logo_id: publicLogoId,
        cover_image_id: publicCoverImageId,
      };
      const addressPayload = {
        line1: street,
        line2: apartment,
        city,
        postal_code: zip,
        state,
        country,
      };
      const openingHoursPayload = opening_hours.every(({ is_closed }) => is_closed)
        ? []
        : buildOpeningHoursPayload(opening_hours);
      const hasLocationChanges =
        locationPayload.name !== normalizeValue(location?.name) ||
        locationPayload.description !== normalizeValue(location?.description) ||
        locationPayload.location_tracking_id !== normalizeValue(location?.location_tracking_id) ||
        locationPayload.facebook !== normalizeValue(location?.facebook) ||
        locationPayload.instagram !== normalizeValue(location?.instagram) ||
        locationPayload.tiktok !== normalizeValue(location?.tiktok) ||
        locationPayload.x !== normalizeValue(location?.x) ||
        locationPayload.pinterest !== normalizeValue(location?.pinterest) ||
        JSON.stringify(locationPayload.additional_links) !==
          JSON.stringify(normalizeAdditionalLinks(location?.additional_links)) ||
        locationPayload.vanity_url !== normalizeValue(location?.vanity_url) ||
        locationPayload.logo_id !== normalizeValue(location?.logo_id) ||
        locationPayload.cover_image_id !== normalizeValue(location?.cover_image_id);
      const hasAddressChanges =
        addressPayload.line1 !== normalizeValue(location?.address?.line1) ||
        addressPayload.line2 !== normalizeValue(location?.address?.line2) ||
        addressPayload.city !== normalizeValue(location?.address?.city) ||
        addressPayload.postal_code !== normalizeValue(location?.address?.postal_code) ||
        addressPayload.state !== normalizeValue(location?.address?.state) ||
        addressPayload.country !== normalizeValue(location?.address?.country);
      const initialOpeningHours = getInitialOpeningHours(location?.opening_hours);
      const initialOpeningHoursPayload = initialOpeningHours.every(({ is_closed }) => is_closed)
        ? []
        : buildOpeningHoursPayload(initialOpeningHours);
      const hasOpeningHoursChanges =
        JSON.stringify(openingHoursPayload) !== JSON.stringify(initialOpeningHoursPayload);
      const requests = [];

      if (hasLocationChanges) {
        requests.push(
          updateLocation({
            uid,
            content: locationPayload,
            skipInvalidation: true,
          }),
        );
      }

      if (hasAddressChanges) {
        requests.push(
          updateLocationAddress({
            uid,
            address: addressPayload,
            skipInvalidation: true,
          }),
        );
      }

      if (hasOpeningHoursChanges) {
        requests.push(
          updateOpeningHours({
            uid,
            openingHours: openingHoursPayload,
            skipInvalidation: true,
          }),
        );
      }

      if (requests.length > 0) {
        await Promise.all(requests);
        queryClient.invalidateQueries({ queryKey: LOCATIONS_KEYS.LOCATIONS_LISTS() });
        queryClient.invalidateQueries({ queryKey: LOCATIONS_KEYS.LOCATION(uid), exact: true });
      }

      closePopup();
    },
    [
      closePopup,
      location,
      queryClient,
      updateLocation,
      updateLocationAddress,
      updateOpeningHours,
      uploadMediaAsync,
    ],
  );

  return (
    <ModalForm
      initialValues={initialValues}
      validationSchema={validationSchema}
      pristine={activeTab !== 'openingHours'}
      isClosePopupOn={false}
      isLoading={isSubmitting}
      ignoreClickedState
      onSubmit={handleSubmit}
      extraButtons={({ values, setFieldValue }) =>
        activeTab === 'openingHours' ? (
          <Button
            type="button"
            variant="link"
            className="mr-3 text-danger text-decoration-underline"
            onClick={() =>
              setFieldValue(
                'opening_hours',
                values.opening_hours.map(({ weekday }) => ({
                  weekday,
                  opens_at: '',
                  closes_at: '',
                  is_closed: true,
                })),
              )
            }
            disabled={isSubmitting}
          >
            {t('openingHours.clear')}
          </Button>
        ) : undefined
      }
      header={
        <ScrollableTabs
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          align="center"
          stretch
        />
      }
    >
      {({ values, setFieldValue, setFieldTouched }) =>
        renderContentForActiveTab(activeTab, values, setFieldValue, setFieldTouched)
      }
    </ModalForm>
  );
};

const Label = styled.label`
  margin-bottom: 16px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
  width: 100%;
`;

const InputWithIconContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: row;
  gap: 8px;
`;

const InputContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const AdditionalLinksContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const AdditionalLinkRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 12px;
  align-items: start;

  ${media.sm`
    grid-template-columns: 1fr 1fr auto;
  `}
`;

const AdditionalLinkField = styled(Label)`
  margin: 0;
`;

const AddLinkButton = styled.button`
  width: 100%;
  min-height: 44px;
  border: 1px dashed #ced4da;
  border-radius: 4px;
  background: #fff;
  color: #495057;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: #f8f9fa;
  }
`;

const AdditionalLinkActions = styled.div`
  align-self: start;
  margin-top: 32px;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const LinkActionButton = styled.button`
  width: 42px;
  height: 44px;
  margin: 0;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  border: none;
  transition: color 0.2s;

  i {
    color: #495057;
  }

  &:hover:not(:disabled) i {
    color: #000;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.35;
  }
`;

const DeleteLinkButton = styled(LinkActionButton)`
  i {
    color: #be1622;
  }

  &:hover:not(:disabled) i {
    color: #a2131d;
  }
`;

const IconContainer = styled.div`
  align-self: flex-start;
  flex-shrink: 0;
  width: 42px;
  aspect-ratio: 1 / 1;
  border-radius: 4px;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ $bgColor }) => $bgColor || '#000'};
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 16px;
`;

const ImagesPreview = styled(LocationImagesPreview)`
  max-width: 600px;
`;

const UploadSection = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 16px;

  ${media.md`
    flex-direction: row;
    gap: 32px;
  `}
`;

const MediaUploadContainer = styled.div`
  flex: 1;
`;

const PrependText = styled.span`
  display: block;
  max-width: 140px;
  min-width: 0;
  flex: 0 1 140px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  ${media.sm`
    max-width: 180px;
    flex-basis: 180px;
  `}

  ${media.md`
    max-width: 200px;
    flex-basis: 200px;
  `}
`;

EditLocationForm.propTypes = {
  location: PropTypes.object.isRequired,
};

export default EditLocationForm;
